import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { onCacheInvalidated, offCacheInvalidated } from '@/utils/cacheEvents';

interface CacheInvalidationState {
  cacheVersion: number;
}

const CacheInvalidationContext = createContext<CacheInvalidationState>({ cacheVersion: 0 });

interface CacheInvalidationProviderProps {
  children: ReactNode;
}

export const CacheInvalidationProvider: React.FC<CacheInvalidationProviderProps> = ({ children }) => {
  const [cacheVersion, setCacheVersion] = useState(0);
  const isInitializedRef = useRef<boolean>(false);

  useEffect(() => {
    if (__DEV__ && !isInitializedRef.current) {
      console.log('[CacheInvalidation] Provider initialized, listening for events');
      isInitializedRef.current = true;
    }

    const handleCacheInvalidated = () => {
      setCacheVersion(prev => {
        const newVersion = prev + 1;
        if (__DEV__) {
          console.log(`[CacheInvalidation] Event received, version: ${newVersion}`);
        }
        return newVersion;
      });
    };

    onCacheInvalidated(handleCacheInvalidated);

    return () => {
      offCacheInvalidated(handleCacheInvalidated);
    };
  }, []);

  return (
    <CacheInvalidationContext.Provider value={{ cacheVersion }}>
      {children}
    </CacheInvalidationContext.Provider>
  );
};

export const useCacheInvalidationContext = (): number => {
  const context = useContext(CacheInvalidationContext);
  return context.cacheVersion;
};
